import { useCallback } from 'react';

import { usePathBuilder } from '@/hooks/usePathBuilder';
import { NavigateToPlp, UseNavigateToPlp } from '@/types/navigation.typs';
import {
  BrowserEvent,
  STORAGE_KEYS,
  STORAGE_MESSAGE_TYPES,
} from '@/utils/constants';
import { postMessageToApp, postMessageToWeb } from '@/utils/window.util';

const MIN_QUERY_LENGTH = 2;

export function useNavigateToPlp({
  lang,
  isNative,
  isInIframe,
  router,
}: UseNavigateToPlp): NavigateToPlp {
  const { buildPlpPagePath } = usePathBuilder();

  const barcodeQuery = router.query?.barcodeQuery as string | undefined;
  const navigateToScreen = router.query?.navigateToScreen as string | undefined;

  return useCallback(
    (searchQuery: string) => {
      if (!searchQuery || searchQuery.trim().length < MIN_QUERY_LENGTH) {
        return;
      }

      const trimmed = searchQuery.trim();

      // Clear multisearch state for normal search
      if (typeof window !== 'undefined') {
        if (isNative) {
          sessionStorage.removeItem(STORAGE_KEYS.IS_MULTI_SEARCH_LIST);
          sessionStorage.removeItem(STORAGE_KEYS.MULTI_SEARCH_QUERIES);
          postMessageToApp({
            type: STORAGE_MESSAGE_TYPES.REMOVE_STORAGE,
            data: { key: STORAGE_KEYS.IS_MULTI_SEARCH_LIST },
          });
        } else {
          sessionStorage.removeItem(STORAGE_KEYS.IS_MULTI_SEARCH_LIST);
          sessionStorage.removeItem(STORAGE_KEYS.MULTI_SEARCH_QUERIES);
        }
      }

      const plpPath = buildPlpPagePath({
        isNative,
        query: trimmed,
        params: {
          ...(navigateToScreen && !isInIframe
            ? {
                navigateToScreen,
                ...(barcodeQuery && barcodeQuery?.toString() === trimmed
                  ? { barcodeQuery }
                  : {}),
              }
            : {}),
        },
      });

      if (isInIframe) {
        postMessageToWeb(
          {
            type: BrowserEvent.NAVIGATE_TO_PLP,
            q: trimmed,
            lang,
            isNative,
            path: plpPath,
          },
          '*'
        );
      } else {
        void router.prefetch?.(plpPath);
        router.push(plpPath);
      }
    },
    [
      buildPlpPagePath,
      router,
      lang,
      isNative,
      isInIframe,
      barcodeQuery,
      navigateToScreen,
    ]
  );
}
